import { useEffect, useState } from 'react';
import { useCurrentHouse } from '@/context/CurrentHouseContext';
import { useHouseRecordUpload } from '@/hooks/house/documents/handlers/useHouseRecordUpload';
import { useQueryClient } from '@tanstack/react-query';
import { refreshDocumentList } from '@/hooks/house/documents/documentListRefresh.ts';
import { USER_NOTE_TAG } from '@/constants/documentTags.ts';
import { toaster } from '@/components/ui/toaster.tsx';
import {
	excludeSystemMetadataFiles,
	MAXIMUM_AMOUNT_OF_FILES,
	MIME_TYPES,
	normalizeFileExtensions,
	SUPPORTED_IMAGE_MIME_TYPES,
	validateFiles,
} from '@/utils/upload/uploadUtils.ts';

export interface RecordComposerController {
	value: string;
	setValue: (value: string) => void;
	isDirty: boolean;
	canSubmit: boolean;
	isSubmitting: boolean;
	isPrivate: boolean;
	togglePrivate: () => void;
	submit: () => void;
	/**
	 * Instant upload of picked files (no form) — mirrors the existing browse/drag flow.
	 * `isPrivate` overrides the composer's toggle, so the drag-and-drop zones can set privacy
	 * by which half the files land on.
	 */
	submitFiles: (files: FileList | File[] | null, isPrivate?: boolean) => void;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const ACCEPTED_RECORD_FILE_TYPES = [
	MIME_TYPES.PDF,
	MIME_TYPES.DOCX,
	MIME_TYPES.XLSX,
	MIME_TYPES.TXT,
	MIME_TYPES.MARKDOWN,
	...SUPPORTED_IMAGE_MIME_TYPES,
];

const getAcceptedTypesDisplay = () =>
	ACCEPTED_RECORD_FILE_TYPES.map((type) => {
		switch (type) {
			case MIME_TYPES.PDF:
				return 'pdf';
			case MIME_TYPES.DOCX:
				return 'docx';
			case MIME_TYPES.XLSX:
				return 'xlsx';
			case MIME_TYPES.TXT:
				return 'txt';
			case MIME_TYPES.MARKDOWN:
				return 'md';
			default:
				return type.startsWith('image/') ? type.split('/')[1] : type;
		}
	}).join(', ');

interface UseRecordComposerOptions {
	/** Called once the note has been accepted, so the host can collapse the composer. */
	onSubmitted: () => void;
	/**
	 * Initial privacy, so a host can default it to its current context (e.g. the records page
	 * defaults to private while the Private filter is active). Re-syncs if it changes.
	 */
	defaultPrivate?: boolean;
}

/**
 * Owns the Records tab's note entry and commit. Reuses the existing
 * `uploadTextRecord` pipeline (the same path the old Note dialog used), so only
 * the presentation moves into the composer — the upload behaviour is unchanged.
 */
export const useRecordComposer = (options: UseRecordComposerOptions): RecordComposerController => {
	const { onSubmitted, defaultPrivate = false } = options;
	const { house } = useCurrentHouse();
	const queryClient = useQueryClient();
	const { uploadTextRecord, uploadFileRecord } = useHouseRecordUpload(house);

	const [value, setValue] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isPrivate, setIsPrivate] = useState(defaultPrivate);

	// Follow the host's default when it changes (e.g. the records privacy filter switches).
	// Only re-runs on an actual change, so a manual toggle within a single context survives.
	useEffect(() => {
		setIsPrivate(defaultPrivate);
	}, [defaultPrivate]);

	const isDirty = value.trim().length > 0;
	const canSubmit = value.trim().length > 0 && !isSubmitting;
	const togglePrivate = () => setIsPrivate((prev) => !prev);

	const submit = async () => {
		const text = value.trim();
		if (!text || isSubmitting) return;

		setIsSubmitting(true);
		try {
			await uploadTextRecord(text, {
				tags: [USER_NOTE_TAG],
				isPrivate,
				onAccepted: async () => {
					setValue('');
					setIsPrivate(defaultPrivate);
					onSubmitted();
				},
				onComplete: async () => {
					// One note is one task, but it shares the refresh budget with any upload in flight.
					refreshDocumentList(queryClient, house.id);
				},
				showNotification: (config) => toaster.create(config),
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const submitFiles = async (files: FileList | File[] | null, isPrivateOverride?: boolean) => {
		if (!files || files.length === 0 || isSubmitting) return;

		// Excluded before validation: a folder drag carries these invisibly, so rejecting them as
		// unsupported types blames the user for a file they never chose and cannot see.
		const droppedFiles = excludeSystemMetadataFiles(Array.from(files));
		if (droppedFiles.length === 0) return;

		const { validFiles, rejectedFiles } = validateFiles(
			droppedFiles,
			MAX_FILE_SIZE,
			MAXIMUM_AMOUNT_OF_FILES,
			[],
			ACCEPTED_RECORD_FILE_TYPES,
		);

		if (rejectedFiles.length > 0) {
			const acceptedTypesDisplay = getAcceptedTypesDisplay();
			const rejectionMessages = rejectedFiles.map(({ file, errors }) => {
				let description = `The file "${file.name}" is invalid.`;

				if (errors.includes('FILE_TOO_LARGE')) {
					description = `The file "${file.name}" is too large. Max size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
				} else if (errors.includes('FILE_EMPTY')) {
					description = `The file "${file.name}" is empty. Empty files are not allowed.`;
				} else if (errors.includes('TOO_MANY_FILES')) {
					description = `Too many files. Maximum ${MAXIMUM_AMOUNT_OF_FILES} files allowed.`;
				} else if (errors.includes('FILE_TYPE_NOT_ALLOWED')) {
					description = `The file "${file.name}" has an unsupported file type. Accepted file types: ${acceptedTypesDisplay}.`;
				}

				return description;
			});

			Array.from(new Set(rejectionMessages)).forEach((description) => {
				toaster.create({
					title: 'File Rejected',
					description,
					type: 'error',
					closable: true,
					duration: 20_000,
				});
			});
		}

		if (validFiles.length === 0) return;

		setIsSubmitting(true);
		try {
			await uploadFileRecord(normalizeFileExtensions(validFiles), {
				isPrivate: isPrivateOverride ?? isPrivate,
				onAccepted: async () => {
					setIsPrivate(defaultPrivate);
					onSubmitted();
				},
				onComplete: async () => {
					// Fires once per task, so a folder drop asks as many times as it holds files.
					refreshDocumentList(queryClient, house.id);
				},
				showNotification: (config) => toaster.create(config),
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return { value, setValue, isDirty, canSubmit, isSubmitting, isPrivate, togglePrivate, submit, submitFiles };
};
